import type { AssetListItem } from '@cartwhl/common/api/getAssets';
import deleteAssetBookmark from '@cartwhl/common/api/deleteAssetBookmark';
import type { AssetPatchState, BookmarkAssetRequest } from '@cartwhl/common/api/assetPatchState';
import patchBatchByID from '@cartwhl/common/api/patchBatchByID';
import patchMediaByID from '@cartwhl/common/api/patchMediaByID';
import patchProjectByID from '@cartwhl/common/api/patchProjectByID';
import patchSceneByID from '@cartwhl/common/api/patchSceneByID';
import putAssetBookmark from '@cartwhl/common/api/putAssetBookmark';

import type { AssetLibraryGroupDetailSelection } from './assetLibraryGroupDetail';
import type { AssetLibraryAssetsLoadState } from './assetLibraryAssetsLoad';
import {
  getActiveAssetsLoad,
  getMainLibraryAssets,
} from './assetLibraryAssetsLoad';
import { isGroupLevelBookmarkGroupID, isProjectGroupID } from './assetLibraryGroupIds';
import type { AssetLibraryDisplayItem } from './utils';
import { shouldShowStackedPreview } from './utils';

export type AssetLibraryDeleteTarget = {
  resource: 'media' | 'scene' | 'batch' | 'project';
  id: string;
  dedupeKey: string;
};

function createDeleteTarget(
  resource: AssetLibraryDeleteTarget['resource'],
  id: string,
): AssetLibraryDeleteTarget {
  return {
    resource,
    id,
    dedupeKey: `${resource}:${id}`,
  };
}

export function resolveGroupIDDeleteTarget(
  groupID: string,
): AssetLibraryDeleteTarget {
  if (isProjectGroupID(groupID)) {
    return createDeleteTarget('project', groupID);
  }

  return createDeleteTarget('batch', groupID);
}

export function resolveGroupSelectionDeleteTarget(
  selection: AssetLibraryGroupDetailSelection,
): AssetLibraryDeleteTarget {
  return resolveGroupIDDeleteTarget(selection.groupID);
}

export function resolveAssetLibraryDeleteTarget(
  asset: AssetLibraryDisplayItem,
): AssetLibraryDeleteTarget | null {
  if (shouldShowStackedPreview(asset) && asset.groupID) {
    return resolveGroupIDDeleteTarget(asset.groupID);
  }

  switch (asset.assetType) {
    case 'MEDIA_IMAGE':
    case 'MEDIA_VIDEO':
      return createDeleteTarget('media', asset.assetID);
    case 'SCENE':
      return createDeleteTarget('scene', asset.assetID);
    case 'MOGEN_JOB':
    case 'MASCOT_JOB': {
      if (!asset.groupID) {
        return null;
      }

      return resolveGroupIDDeleteTarget(asset.groupID);
    }
    default:
      return null;
  }
}

export function dedupeDeleteTargets(
  targets: AssetLibraryDeleteTarget[],
): AssetLibraryDeleteTarget[] {
  const seen = new Set<string>();
  const deduped: AssetLibraryDeleteTarget[] = [];

  for (const target of targets) {
    if (seen.has(target.dedupeKey)) {
      continue;
    }

    seen.add(target.dedupeKey);
    deduped.push(target);
  }

  return deduped;
}

export function assetMatchesDeleteTarget(
  asset: AssetListItem,
  target: AssetLibraryDeleteTarget,
): boolean {
  switch (target.resource) {
    case 'media':
    case 'scene':
      return asset.assetID === target.id;
    case 'batch':
      return asset.groupID === target.id;
    case 'project':
      return asset.groupID === target.id;
    default:
      return false;
  }
}

export const ASSET_LIBRARY_DELETE_OWN_ITEM_TOOLTIP =
  'You can only delete items you created';

export const ASSET_LIBRARY_RESTORE_OWN_ITEM_TOOLTIP =
  'You can only restore items you created';

export type AssetLibraryDeletableItem = Pick<AssetListItem, 'createdByUser'>;

export function canUserDeleteAsset(
  asset: AssetLibraryDeletableItem | undefined,
  userID: string | undefined,
): boolean {
  if (!userID || !asset?.createdByUser) {
    return false;
  }

  return asset.createdByUser === userID;
}

export function canUserDeleteAllAssets(
  assets: AssetLibraryDeletableItem[],
  userID: string | undefined,
): boolean {
  if (!userID || assets.length === 0) {
    return false;
  }

  return assets.every((asset) => canUserDeleteAsset(asset, userID));
}

export function findAssetForGroupID(
  groupID: string,
  state: AssetLibraryAssetsLoadState,
): AssetListItem | undefined {
  const mainAsset = getMainLibraryAssets(state).find(
    (item) => item.groupID === groupID,
  );

  if (mainAsset) {
    return mainAsset;
  }

  return getActiveAssetsLoad(state).data?.items?.find(
    (item) => item.groupID === groupID,
  );
}

export function resolveDeletablePreviewAsset(
  asset: AssetLibraryDisplayItem | null | undefined,
  state: AssetLibraryAssetsLoadState,
): AssetLibraryDeletableItem | undefined {
  if (!asset) {
    return undefined;
  }

  if (asset.createdByUser) {
    return asset;
  }

  return (
    getMainLibraryAssets(state).find((item) => item.assetID === asset.assetID) ??
    getActiveAssetsLoad(state).data?.items?.find(
      (item) => item.assetID === asset.assetID,
    ) ??
    asset
  );
}

export async function patchAssetTarget(
  target: AssetLibraryDeleteTarget,
  input: {
    apiKey: string;
    userID: string;
    state: AssetPatchState;
  },
) {
  const body = {
    state: input.state,
    requestedByUser: input.userID,
  };

  switch (target.resource) {
    case 'media':
      return patchMediaByID(input.apiKey, target.id, body);
    case 'scene':
      return patchSceneByID(input.apiKey, target.id, body);
    case 'batch':
      return patchBatchByID(input.apiKey, target.id, body);
    case 'project':
      return patchProjectByID(input.apiKey, target.id, body);
    default:
      return { error: { message: 'Unsupported delete target.' } };
  }
}

export type AssetLibraryBookmarkRequest = {
  assetID: string;
  groupID?: string;
};

export function resolveAssetLibraryBookmarkRequest(
  asset: AssetLibraryDisplayItem,
): AssetLibraryBookmarkRequest {
  const groupID = asset.groupID;
  const useGroupBookmark =
    Boolean(groupID) &&
    (shouldShowStackedPreview(asset) ||
      ((asset.assetType === 'MOGEN_JOB' || asset.assetType === 'MASCOT_JOB') &&
        isGroupLevelBookmarkGroupID(groupID!)));

  if (useGroupBookmark && groupID) {
    return {
      assetID: asset.assetID,
      groupID,
    };
  }

  return {
    assetID: asset.assetID,
  };
}

export function dedupeBookmarkRequests(
  requests: AssetLibraryBookmarkRequest[],
): AssetLibraryBookmarkRequest[] {
  const seen = new Set<string>();
  const deduped: AssetLibraryBookmarkRequest[] = [];

  for (const request of requests) {
    const dedupeKey = request.groupID
      ? `group:${request.groupID}`
      : `asset:${request.assetID}`;

    if (seen.has(dedupeKey)) {
      continue;
    }

    seen.add(dedupeKey);
    deduped.push(request);
  }

  return deduped;
}

export function assetMatchesBookmarkRequest(
  asset: AssetListItem,
  request: AssetLibraryBookmarkRequest,
): boolean {
  if (request.groupID) {
    return asset.groupID === request.groupID;
  }

  return asset.assetID === request.assetID;
}

export async function setAssetBookmarked(
  request: AssetLibraryBookmarkRequest,
  input: {
    apiKey: string;
    userID: string;
    bookmarked: boolean;
  },
) {
  const body: BookmarkAssetRequest = {
    requestedByUser: input.userID,
    ...(request.groupID ? { groupID: request.groupID } : {}),
  };
  const bookmarkResourceID = request.groupID ?? request.assetID;

  if (input.bookmarked) {
    return putAssetBookmark(input.apiKey, bookmarkResourceID, body);
  }

  return deleteAssetBookmark(input.apiKey, bookmarkResourceID, body);
}
